import {
	ICustomerLanguageInfo,
	IMenuTranslation,
	TFileId,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IComboModifierForDishFullInfo,
	IRestaurantCategory,
	DirectionForPaginated,
	ITranslationDetailedInfo,
	IRestaurantPropertiesModifierGroup,
	EntityStatus,
} from 'types';

// Menu v1
export interface IGetPaginatedDishes {
	pageNumber: number;
	pageSize: number;
	searchByName: Nullable<string>;
	customerId: Nullable<string>;
	restaurantId: Nullable<string>;
	categoryId: Nullable<string>;
	subCategoryId: Nullable<string>;
	priceFrom: null | number;
	priceTo: null | number;
	sortByName: Nullable<string>;
	sortByPrice: Nullable<string>;
	isUnassignedDishes: null | boolean;
	isDisabledDish: null | boolean;
	isShowOnlyComboItems: null | boolean;
}
// Menu v1
// Menu v2
export enum SortByForPaginatedDishes {
	Name = 'Name',
}

export interface IGetPaginatedDishesByCustomerV2 {
	customerId: string;
	pageNumber: number;
	pageSize: number;
	searchInput: Nullable<string>;
	sorting: Nullable<{
		sortBy: SortByForPaginatedDishes;
		direction: DirectionForPaginated;
	}>;
}

export interface IGetPaginatedDishesByRestaurantV2 {
	restaurantId: string;
	categoryId: Nullable<string>;
	subCategoryId: Nullable<string>;
	pageNumber: number;
	pageSize: number;
	searchInput: Nullable<string>;
	assigned: Nullable<boolean>;
	isDisabled: Nullable<boolean>;
	sorting: Nullable<{
		sortBy: SortByForPaginatedDishes;
		direction: DirectionForPaginated;
	}>;
}
// Menu v2

export interface IDishCustomer {
	id: string;
	name: string;
}

export interface IDishComboItem {
	id: string;
	dishId: string;
	modifierId: string;
	dishName: string;
	modifierName: string;
}

export interface IDishPrice {
	id: string;
	type: number;
	priority: number;
	value: number;
	cronCondition: Nullable<string>;
}

export interface IDishSubCategoryForPagination {
	id: string;
	dishId: string;
	positionNumber: number;
	categoryId: string;
	restaurantId: string;
}

export interface IDishSubCategoryForGetInfo {
	restaurantId: string;
	categoryId: string;
	id: string;
	dishId: string;
	positionNumber: number;
	restaurantName: string;
	categoryName: string;
	subCategoryName: string;
	dishName: string;
}

export interface IDishAllSubCategory {
	id: string;
	subCategoryName: Nullable<string>;
	categoryId: string;
	categoryName: string | null;
}

export interface IRestaurantDishSubCategory {
	id: string;
	dishId: string;
	positionNumber: number;
}

export interface IRestaurantDish {
	id: string;
	restaurantId: string;
	price: number;
	isDisabled: boolean;
	restaurantName: string;
	restaurantAddress: string;
	currencySymbol: string | null;
}

export interface ISuggestedDish {
	id: string;
	suggestedId: string;
	suggestedName: string;
	positionNumber: number;
	externalId: Nullable<string>;
}

export interface IDishModification {
	id: string;
	modificationId: string;
	modificationName: string;
	positionNumber: number;
	showInCombo: boolean;
	externalId: Nullable<string>;
}

export interface IAdditionalDish {
	id: string;
	additionalId: string;
	additionalName: string;
	positionNumber: number;
	externalId: Nullable<string>;
}

export interface IDishModifierGroup {
	id: string;
	modifierGroupId: string;
	modifierGroupName: string;
	positionNumber: number;
	upLimit: number | null;
	downLimit: number | null;
	isLimitRequired: boolean;
	isAdditional: boolean;
	isLimitsProtectedFromSync: boolean;
}

export interface IItemDishesForPagination {
	id: string;
	name: Nullable<string>;
	customer: null | IDishCustomer;
	categories: IRestaurantCategory[];
	posName: Nullable<string>;
	filePath: Nullable<string>;
	preSignedUrl: string;
	externalId: Nullable<string>;
	isDisabled: boolean;
	isCombo: boolean;
	isCreatedInPos: boolean;
	articleNumber: Nullable<string>;
	insertedBy: Nullable<string>;
	updatedBy: Nullable<string>;
	insertedDate: string;
	updatedDate: Nullable<string>;
	subCategories: IDishSubCategoryForPagination[];
	allSubCategories: IDishAllSubCategory[];
	restaurantsDishes: IRestaurantDish[];
}

export interface IRestaurantDishForGetInfo {
	id: string;
	restaurantId: string;
	price: number;
	isDisabled: boolean;
	restaurantName: string;
	restaurantStatus: EntityStatus;
	restaurantAddress: string;
	currencySymbol: string | null;
	isCommentTurnOff: boolean;
	isCommentRequired: boolean;
}

export type TDishType = null | 'Dish' | 'Modifier' | 'DishAndModifier';

export type TPortionSelectionMode = 'Undefined' | 'Required' | 'Optional';

export type TPriceStrategy = 'Undefined' | 'Override' | 'Additive';

// Menu v1
export interface IItemDishesForGetInfo {
	id: string;
	name: Nullable<string>;
	categories: IRestaurantCategory[];
	posName: Nullable<string>;
	posShortDescription: Nullable<string>;
	shortDescription: Nullable<string>;
	comboModifierId: Nullable<string>;
	filePath: Nullable<string>;
	preSignedUrl: string;
	smallFilePath: Nullable<string>;
	smallPreSignedUrl: string;
	videoPath: Nullable<string>;
	videoPreSignedUrl: Nullable<string>;
	externalId: Nullable<string>;
	isDisabled: boolean;
	isCombo: boolean;
	isCreatedInPos: boolean;
	articleNumber: Nullable<string>;
	comboItemId: Nullable<string>;
	insertedBy: Nullable<string>;
	updatedBy: Nullable<string>;
	insertedDate: string;
	updatedDate: Nullable<string>;
	activationCron: string | null;
	deactivationCron: string | null;
	comboItem: IDishComboItem;
	subCategories: IDishSubCategoryForGetInfo[];
	allSubCategories: IDishAllSubCategory[];
	restaurantsDishes: IRestaurantDishForGetInfo[];
	suggestedDishes: ISuggestedDish[];
	dishModifications: IDishModification[];
	additionalDishes: IAdditionalDish[];
	modifierGroups: IDishModifierGroup[];
	comboModifier: null | IComboModifierForDishFullInfo;
	customer: ICustomerLanguageInfo | null;
	translations: IMenuTranslation[];
	isTaxPercentProtectedFromSync: boolean;
	taxPercent: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	isFiscalized: boolean;
}

export interface IGetDishInfo {
	data: {
		dish: IItemDishesForGetInfo;
	};
}
// Menu v1
// Menu v2
export interface IItemDishesForGetInfoV2 {
	id: string;
	name: string;
	type: TDishType;
	photoPath: Nullable<string>;
	posName: Nullable<string>;
	description: Nullable<string>;
	externalId: Nullable<string>;
	articleNumber: Nullable<string>;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	overrideScheduleAvailability: boolean;
	isPortionEnabled: boolean;
	isCreatedOnPos: boolean;
	customer: IItemForGetShortInfo;
	restaurants: IItemForGetShortInfoWithStatus[];
	restaurantsWithPortions: IItemForGetShortInfo[];
	suggestedDishes: IItemForGetShortInfo[];
	additionalDishes: IItemForGetShortInfo[];
	dishModifications: IItemForGetShortInfo[];
	translations: ITranslationDetailedInfo[];
}

export interface IGetDishInfoV2 {
	data: IItemDishesForGetInfoV2;
}
// Menu v2

// Menu v1
export interface IDishes {
	dishes: IItemDishesForPagination[];
	dishesCount: number;
}
// Menu v1
// Menu v2
export interface IRestaurantWithDishPrices {
	id: string;
	name: string;
	price: Nullable<number>;
	portionPrices: Nullable<number>[];
	currencySymbol: Nullable<string>;
}

export interface IDishesByCustomerV2 {
	totalCount: number;
	items: {
		id: string;
		name: Nullable<string>;
		articleNumber: Nullable<string>;
		externalId: Nullable<string>;
		restaurants: IRestaurantWithDishPrices[];
		preSignedUrl?: Nullable<string>;
	}[];
}

export interface IDishesByRestaurantV2 {
	totalCount: number;
	items: {
		id: string;
		name: Nullable<string>;
		externalId: Nullable<string>;
		articleNumber: Nullable<string>;
		isDisabled: boolean;
		hasPortions: boolean;
		price: Nullable<number>;
		portionPrices: Nullable<number>[];
		currencySymbol: Nullable<string>;
		subCategories: {
			category: {
				id: string;
				name: Nullable<string>;
			};
			subCategory: {
				id: string;
				name: Nullable<string>;
			};
		}[];
		preSignedUrl?: Nullable<string>;
	}[];
}
// Menu v2

export interface ICategoriesForPaginationDishesForAssign {
	id: string;
	name: Nullable<string>;
}

export interface ISubCategoriesForPaginationDishesForAssign {
	id: string;
	subCategoryName: Nullable<string>;
}

export interface IItemDishesForPaginationForAssign {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	price: number;
	portionPrices: Nullable<number>[];
	currencySymbol: Nullable<string>;
	isCombo: boolean;
	shortDescription: Nullable<string>;
	externalId: Nullable<string>;
	categories: ICategoriesForPaginationDishesForAssign[];
	subCategories: ISubCategoriesForPaginationDishesForAssign[];
}

export interface IDishesForAssign {
	dishes: IItemDishesForPaginationForAssign[];
	dishesCount: number;
}

export interface IGetPaginatedDishesForAssign {
	pageNumber: number;
	pageSize: number;
	searchByName: Nullable<string>;
	restaurantId: string;
	priceFrom: null | number;
	priceTo: null | number;
	isUnassignedDishes: null | boolean;
	sortByName: Nullable<string>;
	sortByPrice: Nullable<string>;
}

export type TDishesSelectedItem = Nullable<string>;

export interface IAdditionalDishInfo {
	externalId: Nullable<string>;
	restaurantsDishes?: IRestaurantDish[];
	isDisabled?: boolean;
}

export interface IPriceForCreateDish {
	priority: string;
	value: string;
	type: string;
	cronCondition: string;
}

export interface ISubCategoryForCreateDish {
	restaurantId: string;
	categoryId: string;
	id: string;
}

export interface IRestaurantForCreateDish {
	restaurantId: string;
	price: number;
	isDisabled: boolean;
	isCommentTurnOff: boolean;
	isCommentRequired: boolean;
}

export interface ITotalSubCategory {
	categoryID: string;
	categoryName: string;
	subCategoryID: string;
	subCategoryName: string;
}

export interface ITotalPrice {
	restaurantId: string;
	restaurantName: string;
	restaurantStatus: EntityStatus;
	price: string;
}

export interface ITotalSuggestedDish {
	id: string;
	name: string;
}

export interface ITotalAdditionalDish {
	id: string;
	name: string;
}

export interface ITotalDishModification {
	id: string;
	name: string;
}

export interface ITotalModifierGroup {
	id: string;
	modifierGroupId: string;
	modifierGroupName: string | null;
	positionNumber: number;
}

export interface IDishModificationWithComboInfo {
	modificationId: string;
	showInCombo: boolean;
}

// Menu v1
export interface ICreateDish {
	name: Nullable<string>;
	shortDescription: Nullable<string>;
	customerId: string;
	fileId: TFileId;
	dishRestaurants: IRestaurantForCreateDish[];
	activationCron: string | null;
	deactivationCron: string | null;
	suggestedDishIds: string[];
	additionalDishIds: string[];
	dishModifications: IDishModificationWithComboInfo[];
	externalId: Nullable<string>;
	translations: IMenuTranslation[];
}
// Menu v1
// Menu v2
export interface ICreateDishV2 {
	fileId: TFileId;
	name: string;
	description: Nullable<string>;
	customerId: string;
	articleNumber: Nullable<string>;
	externalId: Nullable<string>;
	translations: IMenuTranslation[];
	overrideScheduleAvailability: boolean;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	restaurantIds: string[];
	suggestedDishIds: string[];
	additionalDishIds: string[];
	dishModificationIds: string[];
}
// Menu v2

export interface IDishModifierGroupForEdit {
	modifierGroupId: string;
	upLimit: number | null;
	downLimit: number | null;
	isAdditional: boolean;
	isLimitsProtectedFromSync: boolean;
}

// Menu v1
export interface IUpdateDish extends ICreateDish {
	id: string;
	smallFileId: TFileId;
	videoFileId: TFileId;
	modifierGroups: IDishModifierGroupForEdit[];
	comboModifierId: Nullable<string>;
	externalId: Nullable<string>;
	isTaxPercentProtectedFromSync: boolean;
	taxPercent: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	isFiscalized: boolean;
}
// Menu v1
// Menu v2
export interface IUpdateDishV2 {
	id: string;
	fileId: TFileId;
	name: string;
	description: Nullable<string>;
	articleNumber: Nullable<string>;
	externalId: Nullable<string>;
	translations: IMenuTranslation[];
	overrideScheduleAvailability: boolean;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	restaurantIds: string[];
	suggestedDishIds: string[];
	additionalDishIds: string[];
	dishModificationIds: string[];
}
// Menu v2

export interface IAssignmentsForAssignDish {
	subCategoryId: string;
	dishIds: string[];
}

export interface IAssignDish {
	restaurantIds: string[];
	assignments: IAssignmentsForAssignDish[];
}

export interface IAssignDishToSubcategories {
	dishId: string;
	restaurantIds: string[];
	assignments: {
		categoryId: string;
		subCategoryIds: string[];
	}[];
}

export interface IAssignDishToSubcategoriesValidateMetadata {
	categories: { id: string; name: string; restaurants: IItemForGetShortInfo[] }[];
	subCategories: { id: string; name: string; restaurants: IItemForGetShortInfo[] }[];
}

export interface IAssignDishToSubcategoriesValidate {
	data: {
		metadata: IAssignDishToSubcategoriesValidateMetadata;
		success: boolean;
	};
}

export interface IGetDishes {
	data: {
		dishes: IItemDishesForPagination[];
	};
}

export interface IItemForGetShortDishInfo extends IItemForGetShortInfo {
	externalId: Nullable<string>;
}

export interface IGetDishesShort {
	data: {
		dishes: IItemForGetShortDishInfo[];
	};
}

export interface IGetDishesShortByRestaurantId {
	data: {
		restaurantDishes: IItemForGetShortDishInfo[];
	};
}

export interface IGetDishesShortArgs {
	customerId: string;
	isCombo?: boolean;
}

export interface IGetDishesShortBySubCategoryIdsArguments {
	subCategoryIds: string[];
	restaurantIds?: string[];
}

export interface IGetDishesByRestaurantIdsAndSubcategoryIdsArgs {
	restaurantIds: string[];
	subCategoryIds: string[];
}

export interface IDishesByRestaurantIdsAndSubcategoryIdsItem extends IItemForGetShortDishInfo {
	subCategoryNames: string[];
	categoryNames: string[];
	subCategories: {
		categoryId: string;
		categoryName: Nullable<string>;
		subCategoryId: string;
		subCategoryName: Nullable<string>;
	}[];
}

export interface IGetDishesByRestaurantIdsAndSubcategoryIds {
	data: {
		dishes: IDishesByRestaurantIdsAndSubcategoryIdsItem[];
	};
}

// Menu v1
export interface IDishPositionItem {
	id: string;
	name: string | null;
	positionNumber: number;
	filePath: string | null;
	categories: { id: string; name: string }[];
	subCategories: { id: string; name: string }[];
	restaurants: { id: string; name: string; isDisabled: boolean; price: number }[];
}

export interface IGetDishPositionListArguments {
	restaurantId: string;
	categoryId: string;
	subCategoryId: string;
}

export interface IGetDishPositionList {
	data: {
		dishPositions: IDishPositionItem[];
	};
}
// Menu v1
// Menu v2
export interface IDishPositionItemV2 {
	id: string;
	name: Nullable<string>;
	articleNumber: Nullable<string>;
	isDisabled: boolean;
	price: Nullable<number>;
	currencySymbol: string;
	externalId: Nullable<string>;
	positionNumber: number;
	preSignedUrl?: Nullable<string>;
}

export interface IGetDishPositionListArgumentsV2 {
	restaurantId: string;
	subCategoryId: string;
}

export interface IGetDishPositionListV2 {
	data: {
		dishes: IDishPositionItemV2[];
	};
}
// Menu v2

export interface IDishPositionChain {
	id: string;
	name: string;
	address: string;
	status: EntityStatus;
}

// Menu v1
export interface IGetDishPositionChainsArguments {
	restaurantId: string;
	categoryId: string;
	subCategoryId: string;
}
// Menu v1
// Menu v2
export interface IGetDishPositionChainsArgumentsV2 {
	restaurantId: string;
	subCategoryId: string;
}
// Menu v2

export interface IGetDishPositionChains {
	data: {
		restaurantChains: IDishPositionChain[];
	};
}

// Menu v1
export interface IUpdateDishPositions {
	restaurantChains: {
		restaurantId: string;
		categoryId: string;
		subCategoryId: string;
	}[];
	dishIds: string[];
}
// Menu v1
// Menu v2
export interface IUpdateDishPositionsV2 {
	restaurantIds: string[];
	subCategoryId: string;
	dishIds: string[];
}
// Menu v2

export interface IGetDishRestaurantPropertiesArguments {
	restaurantId: string;
	dishId: string;
}

export interface IGetDishRestaurantProperties {
	data: {
		id: string;
		name: Nullable<string>;
		customer: IItemForGetShortInfo;
		restaurantId: Nullable<string>;
		currencySymbol: Nullable<string>;
		price: Nullable<number>;
		isDisabled: boolean;
		isCommentVisible: boolean;
		isFiscalized: boolean;
		isCreatedFromPos: boolean;
		hasOpenPrice: boolean;
		openPrice: Nullable<number>;
		openPriceMaxThreshold: Nullable<number>;
		isTaxPercentProtectedFromSync: boolean;
		taxPercent: Nullable<number>;
		activationCron: Nullable<string>;
		deactivationCron: Nullable<string>;
		hasPortions: boolean;
		portionSelectionMode: TPortionSelectionMode;
		hasPortionBasedModGroups: boolean;
		categories: {
			id: string;
			name: string;
			subCategories: IItemForGetShortInfo[];
		}[];
		modifierGroups: IRestaurantPropertiesModifierGroup[];
		sizes: {
			id: string;
			name: Nullable<string>;
			price: Nullable<number>;
			priceStrategy: TPriceStrategy;
		}[];
	};
}

export interface IUpdateDishRestaurantProperties {
	dishId: string;
	restaurantId: string;
	price: Nullable<number>;
	isDisabled: boolean;
	isCommentVisible: boolean;
	isFiscalized: boolean;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	isTaxPercentProtectedFromSync: boolean;
	taxPercent: Nullable<number>;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	portionSelectionMode: TPortionSelectionMode;
	assignments: {
		categoryId: string;
		subCategoryIds: string[];
	}[];
	modifierGroups: {
		id: string;
		upLimit: Nullable<number>;
		downLimit: Nullable<number>;
	}[];
}

export interface IGetListRestaurantsForAssignDishesArguments {
	customerId: string;
	subCategoryIds: string[];
	dishIds?: string[];
}

export interface IGetListRestaurantsForAssignDishes {
	data: {
		restaurants: IDishPositionChain[];
	};
}

export interface IDataGetCategoriesByIDRestaurantForAssignDishes {
	id: string;
	name: Nullable<string>;
	hasAssignedDishes: boolean;
	filePath: Nullable<string>;
}

export interface IGetCategoriesByIDRestaurantForAssignDishes {
	data: {
		categories: IDataGetCategoriesByIDRestaurantForAssignDishes[];
	};
}

export interface IGetSubcategoriesByIDsCategoriesArguments {
	restaurantId: string;
	categoryIds: string[];
}

export interface ISubcategoriesIDsCategoriesForAssignDishes {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	hasAssignedDishes: boolean;
}

export interface IDataGetSubcategoriesByIDsCategoriesForAssignDishes {
	id: string;
	name: Nullable<string>;
	subCategories: ISubcategoriesIDsCategoriesForAssignDishes[];
}

export interface IGetSubcategoriesByIDsCategoriesForAssignDishes {
	data: {
		categories: IDataGetSubcategoriesByIDsCategoriesForAssignDishes[];
	};
}

export interface IGetDishesByIDsSubcategoriesArguments {
	restaurantId: string;
	subCategoryIds: string[];
}

export interface IDishesIDsSubcategoriesForAssignDishes {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	categories: string[];
	subCategories: string[];
	price: number;
	externalId: Nullable<string>;
}

export interface IDataGetDishesByIDsSubcategoriesForAssignDishes {
	id: string;
	name: Nullable<string>;
	dishes: IDishesIDsSubcategoriesForAssignDishes[];
}

export interface IGetDishesByIDsSubcategoriesForAssignDishes {
	data: {
		subCategories: IDataGetDishesByIDsSubcategoriesForAssignDishes[];
	};
}

export type TPropertiesOfPatchChangesForDish =
	| 'activation'
	| 'deactivation'
	| 'price'
	| 'taxRate'
	| 'openPrice'
	| 'isDisabled'
	| 'comments'
	| 'isFiscalized'
	| 'protectFromSync';

export interface IPropertiesOfPatchChangesForDish {
	id: TPropertiesOfPatchChangesForDish;
	name: TPropertiesOfPatchChangesForDish;
}

export interface IUpdateRestaurantPropertiesAsPatchForDish {
	dishId: string;
	restaurantIds: string[];
	update: {
		activationCron: {
			shouldUpdate: boolean;
			value: string;
		};
		deactivationCron: {
			shouldUpdate: boolean;
			value: string;
		};
		price: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		taxPercent: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		hasOpenPrice: {
			shouldUpdate: boolean;
			value: boolean;
		};
		openPrice: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		openPriceMaxThreshold: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		isDisabled: {
			shouldUpdate: boolean;
			value: boolean;
		};
		isCommentVisible: {
			shouldUpdate: boolean;
			value: boolean;
		};
		isFiscalized: {
			shouldUpdate: boolean;
			value: boolean;
		};
		isTaxPercentProtectedFromSync: {
			shouldUpdate: boolean;
			value: boolean;
		};
	};
}

export interface IDishPortionModifierGroup {
	id: string;
	name: string;
	modifiers: IItemForGetShortInfo[];
}

export interface IDishPortionForGetInfo {
	id: string;
	name: string;
	posName: Nullable<string>;
	price: Nullable<number>;
	currencySymbol: Nullable<string>;
	articleNumber: Nullable<string>;
	externalId: string;
	isProtectedFromSync: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
	isCreatedFromPos: boolean;
	customer: IItemForGetShortInfo;
	restaurant: IItemForGetShortInfo;
	dish: IItemForGetShortInfo;
	modifierGroups: IDishPortionModifierGroup[];
	translations: ITranslationDetailedInfo[];
}

export interface IGetDishPortionInfo {
	data: IDishPortionForGetInfo;
}

export interface ICreatePortion {
	name: string;
	dishId: string;
	externalId: string;
	restaurantIds: string[];
	price: Nullable<number>;
	isProtectedFromSync: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
	translations: IMenuTranslation[];
}

export interface IGetDishRestaurantPortionsArguments {
	restaurantId: string;
	dishId: string;
}

export interface IGetDishRestaurantPortion {
	id: string;
	name: string;
	posName: Nullable<string>;
	price: Nullable<number>;
	currencySymbol: string;
	articleNumber: Nullable<string>;
	externalId: Nullable<string>;
	isProtectedFromSync: boolean;
	isCreatedFromPos: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
}

export interface IGetDishRestaurantPortions {
	data: IGetDishRestaurantPortion[];
}

export interface IToggleDishPortionsEnabledArguments {
	dishId: string;
	isPortionEnabled: boolean;
}

export interface IDeleteDishPortionArguments {
	restaurantId: string;
	portionId: string;
}

export interface IUpdatePortion {
	portionId: string;
	name: string;
	externalId: Nullable<string>;
	restaurantIds: string[];
	price: Nullable<number>;
	isProtectedFromSync: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
	translations: IMenuTranslation[];
}

export interface IPortionPositionChain {
	id: string;
	name: string;
	address: string;
	status: EntityStatus;
}

export interface IGetPortionPositionListArguments {
	restaurantId: string;
	dishId: string;
}

export interface IGetPortionPositionChains {
	data: IPortionPositionChain[];
}

export interface IUpdatePortionPositions {
	restaurantIds: string[];
	dishId: string;
	restaurantDishPortionIds: string[];
}

export interface IPortionPositionItem {
	id: string;
	name: Nullable<string>;
	posName: Nullable<string>;
	price: Nullable<number>;
	currencySymbol: Nullable<string>;
	articleNumber: Nullable<string>;
	externalId: Nullable<string>;
	isDisabled: boolean;
	isProtectedFromSync: boolean;
	isCreatedFromPos: boolean;
	preSignedUrl?: Nullable<string>;
}

export interface IGetPortionPositionList {
	data: IPortionPositionItem[];
}
